import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Star, Share2, Award, MapPin } from "lucide-react";
import Messages from "@/components/Messages";
import LiveDeliveryMap from "@/components/LiveDeliveryMap";
import TestimonialForm from "@/components/TestimonialForm";
import OnboardingTracker from "@/components/OnboardingTracker";
import MemberLedger from "@/components/MemberLedger";

function geocodeFallback(city) {
  if (!city) return { lat: 37.7749, lng: -122.4194 };
  let h = 0;
  for (let i = 0; i < city.length; i++) h = (h << 5) - h + city.charCodeAt(i);
  const lat = 36 + ((Math.abs(h) % 700) / 100);
  const lng = -100 + ((Math.abs(h >> 3) % 2500) / 100);
  return { lat, lng };
}

function ClientLiveTracker({ order }) {
  const [driverPos, setDriverPos] = useState(null);
  const pickup = geocodeFallback(order.pickup_city);
  const dropoff = geocodeFallback(order.dropoff_city);

  useEffect(() => {
    let stop = false;
    const fetchLoc = async () => {
      try {
        const { data } = await api.get(`/orders/${order.order_id}/location`);
        if (!stop && data.driver_lat) setDriverPos({ lat: data.driver_lat, lng: data.driver_lng });
      } catch {}
    };
    fetchLoc();
    const t = setInterval(fetchLoc, 8000);
    return () => { stop = true; clearInterval(t); };
  }, [order.order_id]);

  return (
    <div className="mt-4">
      <LiveDeliveryMap pickup={pickup} dropoff={dropoff} driver={driverPos} />
      <div className="mt-2 text-xs font-sans flex items-center gap-2" data-testid={`client-tracking-${order.order_id}`}>
        <span className={`w-2 h-2 rounded-full ${driverPos ? "bg-emerald-700 animate-pulse" : "bg-stone-400"}`} />
        {driverPos ? "Live — your driver is en route" : "Waiting for driver location..."}
      </div>
    </div>
  );
}

export default function ClientDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [openRate, setOpenRate] = useState(null);
  const [rating, setRating] = useState(5);
  const [feedback, setFeedback] = useState("");
  const [openWholesale, setOpenWholesale] = useState(false);
  const [whForm, setWhForm] = useState({ business_name: "", business_type: "store", expected_volume: "", contact_phone: "", state: user?.state || "", notes: "" });
  const [states, setStates] = useState([]);

  const load = async () => {
    const [o, s] = await Promise.all([api.get("/orders"), api.get("/states")]);
    setOrders(o.data); setStates(s.data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const memberSince = user.member_since ? new Date(user.member_since) : new Date();

  const submitRating = async () => {
    await api.put(`/orders/${openRate}/status`, { status: "delivered", rating, feedback });
    toast.success("Thanks for the feedback!");
    setOpenRate(null);
    load();
  };

  const submitWholesale = async () => {
    await api.post("/wholesale/apply", whForm);
    toast.success("Wholesale application submitted — admins will reach out.");
    setOpenWholesale(false);
  };

  const referralLink = `${window.location.origin}/?ref=${user.user_id}`;
  const copyRef = () => {
    navigator.clipboard?.writeText(referralLink);
    toast.success("Referral link copied.");
  };

  const stat = (label, value, testid) => (
    <div data-testid={testid} className="bg-white border border-stone-200 p-6 rounded-md">
      <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">{label}</div>
      <div className="mt-3 font-serif text-3xl text-stone-900">{value}</div>
    </div>
  );

  return (
    <div className="space-y-8">
      <OnboardingTracker />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {stat("Orders", orders.length, "stat-orders")}
        {stat("Member Since", memberSince.getFullYear(), "stat-member-since")}
        {stat("Active Deliveries", orders.filter((o) => o.status !== "delivered").length, "stat-active")}
        {stat("Membership", user.membership?.label || "Browse", "stat-membership")}
      </div>

      <div className="bg-stone-900 rounded-md p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          <Award className="text-amber-300" size={36} />
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-300 font-semibold">Certificate of Completion</div>
            <div className="font-serif text-2xl text-stone-50 mt-1">Welcome to Azure Gypsy Farms, {user.name?.split(" ")[0]}.</div>
            <div className="font-sans text-sm text-stone-300 mt-1">You're now part of the network. Start ordering anytime.</div>
          </div>
        </div>
        <Button data-testid="refer-btn" onClick={copyRef} variant="outline" className="border-stone-50/30 bg-white/10 text-stone-50 hover:bg-white/20">
          <Share2 size={16} className="mr-2" />Copy Referral Link
        </Button>
      </div>

      <Tabs defaultValue="orders">
        <div className="overflow-x-auto -mx-2 px-2">
          <TabsList className="bg-stone-100 flex-nowrap whitespace-nowrap w-max sm:w-auto">
            <TabsTrigger data-testid="tab-orders" value="orders">My Orders</TabsTrigger>
            <TabsTrigger data-testid="tab-wholesale" value="wholesale">Wholesale</TabsTrigger>
            <TabsTrigger data-testid="tab-deposits" value="deposits">Deposits</TabsTrigger>
            <TabsTrigger data-testid="tab-referrals" value="referrals">Referrals</TabsTrigger>
            <TabsTrigger data-testid="tab-feedback" value="feedback">Feedback</TabsTrigger>
            <TabsTrigger data-testid="tab-messages" value="messages">Messages</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="orders" className="mt-6 space-y-4">
          {orders.length === 0 ? (
            <div className="bg-white border border-stone-200 p-10 text-center rounded-md font-sans text-stone-500">No orders yet — head to the marketplace.</div>
          ) : orders.map((o) => (
            <div key={o.order_id} className="bg-white border border-stone-200 rounded-md p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="font-serif text-xl text-stone-900">{o.product_title}</div>
                  <div className="text-sm text-stone-500 font-sans mt-1">From {o.farmer_name} · {o.pickup_city} → {o.dropoff_city}</div>
                  {o.driver_name && <div className="text-xs text-stone-500 font-sans mt-1">Driver: {o.driver_name}</div>}
                </div>
                <div className="flex items-center gap-3">
                  <Badge className="bg-stone-800 text-stone-50">{o.status}</Badge>
                  {o.status === "delivered" && !o.rating && (
                    <Button data-testid={`rate-${o.order_id}`} onClick={() => { setOpenRate(o.order_id); setRating(5); setFeedback(""); }} variant="outline" className="border-stone-300">
                      <Star size={14} className="mr-2" />Rate
                    </Button>
                  )}
                </div>
              </div>
              {["accepted", "picked_up", "in_transit"].includes(o.status) && o.driver_id && (
                <ClientLiveTracker order={o} />
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="wholesale" className="mt-6">
          <div className="bg-white border border-stone-200 p-8 rounded-md">
            <div className="font-serif text-2xl text-stone-900">Become a Wholesale Buyer</div>
            <p className="mt-2 text-stone-600 font-sans text-sm max-w-2xl">
              Buy in bulk for your family, store, or community group. Wholesale applications are reviewed and tracked by Azure Gypsy admins, and connect you directly with hobby farmers in your state.
            </p>
            <Dialog open={openWholesale} onOpenChange={setOpenWholesale}>
              <DialogTrigger asChild>
                <Button data-testid="apply-wholesale-btn" className="mt-5 bg-emerald-800 hover:bg-emerald-700 text-stone-50">Apply for Wholesale</Button>
              </DialogTrigger>
              <DialogContent className="bg-stone-50 max-w-2xl">
                <DialogHeader><DialogTitle className="font-serif text-2xl">Wholesale Onboarding</DialogTitle></DialogHeader>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input data-testid="wh-name" placeholder="Business / Family name" value={whForm.business_name} onChange={(e) => setWhForm({ ...whForm, business_name: e.target.value })} className="bg-white" />
                  <Select value={whForm.business_type} onValueChange={(v) => setWhForm({ ...whForm, business_type: v })}>
                    <SelectTrigger data-testid="wh-type" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="store">Store</SelectItem>
                      <SelectItem value="family">Family</SelectItem>
                      <SelectItem value="individual">Individual</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input data-testid="wh-volume" placeholder="Expected weekly volume" value={whForm.expected_volume} onChange={(e) => setWhForm({ ...whForm, expected_volume: e.target.value })} className="bg-white" />
                  <Input data-testid="wh-phone" placeholder="Phone" value={whForm.contact_phone} onChange={(e) => setWhForm({ ...whForm, contact_phone: e.target.value })} className="bg-white" />
                  <Select value={whForm.state} onValueChange={(v) => setWhForm({ ...whForm, state: v })}>
                    <SelectTrigger data-testid="wh-state" className="bg-white"><SelectValue placeholder="State" /></SelectTrigger>
                    <SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Textarea data-testid="wh-notes" placeholder="Anything we should know?" value={whForm.notes} onChange={(e) => setWhForm({ ...whForm, notes: e.target.value })} className="bg-white sm:col-span-2" />
                </div>
                <DialogFooter><Button data-testid="wh-submit" onClick={submitWholesale} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">Submit Application</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </TabsContent>

        <TabsContent value="referrals" className="mt-6">
          <div className="bg-white border border-stone-200 rounded-md p-8">
            <div className="font-serif text-2xl text-stone-900">Refer Friends (Meat Buyers)</div>
            <p className="mt-2 text-stone-600 font-sans text-sm max-w-xl">
              Share your referral link. When a hobby farmer confirms a new meat buyer, you'll be recognized in the community.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Input data-testid="ref-link" readOnly value={referralLink} className="bg-stone-50" />
              <Button data-testid="ref-copy" onClick={copyRef} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">Copy</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="deposits" className="mt-6">
          <MemberLedger />
        </TabsContent>

        <TabsContent value="messages" className="mt-6">
          <Messages />
        </TabsContent>
      </Tabs>

      <Dialog open={!!openRate} onOpenChange={(o) => !o && setOpenRate(null)}>
        <DialogContent className="bg-stone-50">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Rate this delivery</DialogTitle></DialogHeader>
          <div className="flex gap-2 items-center">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`star-${n}`} onClick={() => setRating(n)} className={n <= rating ? "text-amber-500" : "text-stone-300"}>
                <Star size={28} fill="currentColor" />
              </button>
            ))}
          </div>
          <Textarea data-testid="feedback-text" placeholder="Feedback for driver..." value={feedback} onChange={(e) => setFeedback(e.target.value)} className="bg-white" />
          <DialogFooter><Button data-testid="rate-submit" onClick={submitRating} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">Submit Rating</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
